import { type ReactNode, useEffect, useRef } from 'react';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { ErrorBoundary as ErrorBoundary_ } from '@gonfalon/error-boundaries/ErrorBoundary';
import { isRESTAPIError } from '@gonfalon/errors/RESTAPIError';
import { isFlagError } from '@gonfalon/flags/errors';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Alert, Button } from '@launchpad-ui/components';
import nullthrows from 'nullthrows';

import { clearAiMonitoringStorage } from '#components/AiMonitoring/util/clearAiMonitoringStorage';
import { useCurrentEnvironmentKey } from '#reducers/projects';

export function SyncReduxWithProjectContext({ children }: { children: ReactNode }) {
  const context = useProjectContext();
  const legacyCurrentEnvironmentKey = useCurrentEnvironmentKey();
  const previousProjectKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const {
      context: { projectKey, selectedEnvironmentKey },
      environments,
      project: project_,
    } = context;

    const currentEnvironment = environments.find((env) => env.key === selectedEnvironmentKey);

    // Clear AI Monitoring storage only when project changes (not environment changes)
    if (previousProjectKeyRef.current !== null && previousProjectKeyRef.current !== projectKey) {
      clearAiMonitoringStorage();
    }
    previousProjectKeyRef.current = projectKey;

    window.__resetStore({
      condition: ({ currentProjectKey, currentEnvironmentKey }) =>
        currentProjectKey !== projectKey || currentEnvironmentKey !== selectedEnvironmentKey,
      payload: {
        initialState: {
          currentProject: project_,
          currentEnvironment: nullthrows(currentEnvironment, 'Expected current environment'),
        },
      },
    });
  }, [context]);

  if (legacyCurrentEnvironmentKey !== context.context.selectedEnvironmentKey) {
    return null;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
}

function ErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary_ FallbackComponent={Fallback} severity="high" ignoredStatuses={[404]}>
      {children}
    </ErrorBoundary_>
  );
}

function Fallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  if (isFlagError(error)) {
    switch (error.code) {
      case 'missing_environment':
        return (
          <Alert variant="inline" status="error">
            {error.message}
            <Button size="small" onPress={() => resetErrorBoundary()}>
              {msg('flags.flagErrorBoundary.tryAgain')}
            </Button>
          </Alert>
        );
      default:
        return (
          <div>
            <Message id="flags.flagErrorBoundary.genericError" />
          </div>
        );
    }
  }
  if (isRESTAPIError(error) && error.status === 404) {
    return (
      <div>
        <Message id="flags.flagErrorBoundary.notFoundError" />
      </div>
    );
  }
  return (
    <div>
      <Message id="flags.flagErrorBoundary.genericError" />
    </div>
  );
}
